/**
 * Module dependencies.
 */

import {
  Box,
  Container,
  Type,
  color,
  media,
  units
} from '@untile/react-components';

import { Button, Loading, Subtitle } from 'src/components/core';
import { Fragment, useEffect } from 'react';
import { GetStaticProps, NextPage } from 'next/types';
import { NewsExhibitionsProps } from 'src/types/news-exhibitions';
import { PageFields } from 'src/types/page-fields';
import {
  fetchNewsExhibitions,
  paginationLimit
} from 'src/api/news-exhibitions/fetch-news-exhibitions';

import { fetchPageFields } from 'src/api/page-fields/fetch-page-fields';
import { ifProp, theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useRouter } from 'next/router';
import Metatags from 'src/components/core/metatags';
import NewsExhibitionsFilters from 'src/components/pages/news-exhibitions/filters';
import NewsExhibitionsList from 'src/components/pages/news-exhibitions/list';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useNewsExhibitions from 'src/api/news-exhibitions/use-news-exhibitions';
import usePageFields from 'src/api/page-fields/use-page-fields';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  data: PageFields;
  newsExhibitions: {
    pageParams: number[];
    pages: Array<{
      data: NewsExhibitionsProps[];
      nextPage: number | null;
      total: number;
    }>;
  };
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section<{ isLoading: boolean }>`
  background-color: ${color('backgrounds.taupe01')};
  padding: ${units(14)} 0 ${units(5)};
  position: relative;

  ${media.min('md')`
    padding: ${units(20)} 0 ${units(18)};
  `}

  ${ifProp(
    'isLoading',
    css`
      min-height: 100vh;
    `
  )}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title    .'
    '. subtitle .'
    '. button   .'
    '. filters  .'
    '. news     .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      '. title    title    .        .'
      '. subtitle .        button   .'
      '. filters  filters  filters  .'
      '. news     news     news     .';
    grid-template-columns: 1fr 4fr 1fr 5fr 1fr;
    grid-template-rows: repeat(4, max-content);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Subtitle)`
  grid-area: subtitle;

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  grid-area: button;

  ${media.min('md')`
    align-self: flex-end;
    display: flex;
    justify-self: flex-end;
  `}
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.div`
  display: grid;
  grid-area: news;
  grid-gap: ${units(3)};
  grid-template-columns: 1fr;
  padding-top: ${units(6)};

  ${media.min('ms')`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('md')`
    grid-gap: ${units(4)} 25px;
    padding-top: ${units(8)};
  `}
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  grid-column: 1 / -1;
  text-align: center;
`;

/**
 * `LoadMore` styled component.
 */

const LoadMore = styled.div`
  grid-column: 1 / -1;
  padding-top: 66px;
  text-align: center;

  ${media.min('ms')`
    padding-top: ${units(11)};
  `}
`;

/**
 * `NewsPage` page.
 */

const NewsPage: NextPage<Props> = props => {
  const { translate } = useTranslate();
  const { query } = useRouter();
  const {
    data: newsHeaderData,
    isLoading: isLoadingHeaderData,
    isSuccess: isSuccessHeaderData
  } = usePageFields({ initialData: props.data });

  const {
    data: newsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoadingNews,
    isSuccess: isSuccessNews,
    refetch
  } = useNewsExhibitions({
    filterParams: query,
    initialData: props?.newsExhibitions
  });

  useEffect(() => {
    refetch();
  }, [query, refetch]);

  const routeResolver = useLocalizedRoute();
  const isUpdating = isFetchingNextPage || isFetching;
  const isLoading = isLoadingHeaderData || isLoadingNews;
  const isSuccess = isSuccessHeaderData && isSuccessNews;
  const total = newsData?.pages[0]?.total ?? 0;
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <>
      <Metatags
        description={newsHeaderData?.newsDescription}
        pageTitle={newsHeaderData?.newsTitle}
        title={newsHeaderData?.newsTitle}
      />

      <Wrapper isLoading={isLoading}>
        <Loading active={isLoading || isUpdating} />

        {isSuccess && (
          <StyledContainer>
            <Grid>
              <Title {...animProps}>{newsHeaderData?.newsTitle}</Title>

              <Description {...animProps}>
                {newsHeaderData?.newsDescription}
              </Description>

              <ButtonWrapper {...animProps}>
                <Button
                  colorTheme={'secondary'}
                  href={routeResolver('contactUs')}
                  size={'large'}
                >
                  {translate('common:actions.reachOutToUs')}
                </Button>
              </ButtonWrapper>

              <Box
                {...animProps}
                gridArea={'filters'}
              >
                <NewsExhibitionsFilters />
              </Box>

              <ListWrapper>
                {!isLoadingNews && !isUpdating && total === 0 && (
                  <Empty {...animProps}>
                    <Type.H6 paddingVertical={units(1)}>
                      {translate('news:list.emptyList')}
                    </Type.H6>
                  </Empty>
                )}

                {total > 0 && (
                  <>
                    {map(newsData?.pages, ({ data }, index) => (
                      <Fragment key={index}>
                        <NewsExhibitionsList newsExhibitions={data} />
                      </Fragment>
                    ))}

                    {hasNextPage && (
                      <LoadMore {...animProps}>
                        <Button
                          colorTheme={'secondary'}
                          disabled={isUpdating}
                          isLoading={isUpdating}
                          onClick={() => fetchNextPage()}
                          size={'large'}
                        >
                          {translate('common:actions.loadMore')}
                        </Button>
                      </LoadMore>
                    )}
                  </>
                )}
              </ListWrapper>
            </Grid>
          </StyledContainer>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => {
  const pageFields = await fetchPageFields({ locale });
  const newsExhibitions = await fetchNewsExhibitions({
    locale,
    params: {
      limit: paginationLimit,
      page: 1
    }
  });

  if (!pageFields || !newsExhibitions) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data: pageFields,
      newsExhibitions: {
        pageParams: [1],
        pages: [newsExhibitions]
      }
    },
    revalidate: 60
  };
};

/**
 * Export `NewsPage` page.
 */

export default NewsPage;
